import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, ChevronRight, MapPin } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import Header from "@/components/public/Header";
import Footer from "@/components/public/Footer";
import { useLead } from "@/components/public/LeadDialog";
import { Picture } from "@/components/public/Picture";
import ContentSections from "@/components/public/ContentSections";
import { Spinner, NotFound } from "./LocationDetail";

export default function WorkspaceDetail() {
  const { slug } = useParams();
  useSeo(`/workspaces/${slug}`);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({});
  const [notFound, setNotFound] = useState(false);
  const { openLead } = useLead();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/public/workspaces/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
    api.get("/public/homepage").then((r) => setSettings(r.data.settings));
  }, [slug]);

  if (notFound) return <NotFound label="Workspace" />;
  if (!data) return <Spinner />;
  const { workspace: w, locations } = data;

  return (
    <div className="bg-white min-h-screen">
      <Header phone={settings.phone} />
      <nav className="container-cz pt-6 flex items-center gap-1.5 text-sm text-slate-500" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-primary">Home</Link><ChevronRight className="w-3.5 h-3.5 text-slate-300" />
        <Link to="/workspaces" className="hover:text-primary">Workspaces</Link><ChevronRight className="w-3.5 h-3.5 text-slate-300" />
        <span className="text-slate-900 font-medium">{w.title}</span>
      </nav>

      <div className="container-cz pt-8 pb-12 grid lg:grid-cols-2 gap-10 items-start">
        <div className="rounded-3xl overflow-hidden shadow-cz aspect-[4/3]">
          <img src={w.image} alt={w.image_alt || w.title} className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="eyebrow mb-3">Workspace Type</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900">{w.title}</h1>
          <p className="text-lg text-slate-600 mt-4">{w.long_description || w.description}</p>
          <div className="grid sm:grid-cols-2 gap-2.5 mt-6">
            {(w.amenities || []).map((a) => (
              <div key={a} className="flex items-center gap-2 text-sm text-slate-700">
                <span className="w-6 h-6 rounded-full bg-sky-100 text-primary flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5" /></span>{a}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button onClick={() => openLead("quote", { workspace: w.slug, space_type: w.title })} data-testid="ws-get-quote" className="rounded-full h-12 px-7 gap-2">Get a Quote <ArrowRight className="w-4 h-4" /></Button>
            <Button onClick={() => openLead("tour", { workspace: w.slug, space_type: w.title })} data-testid="ws-book-tour" variant="outline" className="rounded-full h-12 px-7 border-slate-300">Book a Tour</Button>
          </div>
        </div>
      </div>

      {w.intro && (
        <div className="container-cz pb-4 -mt-2 max-w-3xl">
          <p className="text-slate-600 text-lg leading-relaxed whitespace-pre-line" data-testid="ws-intro">{w.intro}</p>
        </div>
      )}
      <ContentSections sections={w.content_sections} />

      {locations?.length > 0 && (
        <div className="container-cz pb-20">
          <h2 className="font-display text-2xl font-bold text-slate-900 mb-5">Available at These Locations</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((l) => (
              <button key={l.id} onClick={() => navigate(`/locations/${l.slug}`)} className="group card-cz overflow-hidden text-left hover:shadow-cz transition-all">
                <div className="aspect-[16/10] overflow-hidden"><img src={l.image} alt={l.image_alt || l.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-4"><h3 className="font-semibold text-slate-900">{l.name}</h3><p className="text-sm text-primary flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5" /> {l.sector}</p></div>
              </button>
            ))}
          </div>
        </div>
      )}
      <Footer settings={settings} />
    </div>
  );
}
